import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { useAuth } from '../../contexts/SecurityContext';
import LoginForm from '../content-types/LoginForm';

const ProtectedRoute = ({ children, requiredRole = null, requiredRoles = null }) => {
  const { isAuthenticated, loading, hasRole, hasAnyRole, canAccessRoute } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <Spinner animation="border" role="status" className="mb-3">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p>Checking authentication...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  // If not authenticated, show login form
  if (!isAuthenticated) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h4 className="mb-0">Authentication Required</h4>
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-4">
                  You need to be logged in to access this page.
                </p>
                <LoginForm onSignIn={() => window.location.reload()} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Check route-based access
  if (!canAccessRoute(location.pathname)) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card className="border-danger">
              <Card.Header className="bg-danger text-white">
                <h4 className="mb-0">Access Denied</h4>
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-3">
                  You don't have permission to access this page.
                </p>
                <p className="small">
                  <strong>Required access:</strong>
                  {location.pathname.startsWith('/admin') && ' Administrator privileges'}
                  {location.pathname.startsWith('/review') && ' Reviewer or Administrator privileges'}
                  {location.pathname.startsWith('/user') && ' User account'}
                </p>
                <Navigate to="/" replace />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Check specific role requirements
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card className="border-warning">
              <Card.Header className="bg-warning">
                <h4 className="mb-0">Insufficient Privileges</h4>
              </Card.Header>
              <Card.Body>
                <p className="text-muted">
                  You need <strong>{requiredRole}</strong> privileges to access this page.
                </p>
                <Navigate to="/" replace />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Check multiple role requirements (user needs ANY of these roles)
  if (requiredRoles && !hasAnyRole(requiredRoles)) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card className="border-warning">
              <Card.Header className="bg-warning">
                <h4 className="mb-0">Insufficient Privileges</h4>
              </Card.Header>
              <Card.Body>
                <p className="text-muted">
                  You need one of the following privileges: <strong>{requiredRoles.join(', ')}</strong>
                </p>
                <Navigate to="/" replace />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // User has access, render the protected content
  return children;
};

export default ProtectedRoute;
